import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import type { Observable } from 'rxjs';
import { BehaviorSubject } from 'rxjs';

import { RequisitionsListParam } from '@core/requisitions-types';
import type { RequisitionsGroupKind, RequisitionsParams } from '@core/requisitions-types';
import { SortDirection } from '@core/search-types';

import type {
  RequisitionsListViewType,
  RequisitionsTableColumn,
  RequisitionsViewState,
} from '../requisitions.types';
import { RequisitionsListCacheService } from './requisitions-list-cache.service';

@Injectable()
export class RequisitionsListViewStateService {
  private readonly cacheService = inject(RequisitionsListCacheService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  public viewState$: Observable<RequisitionsViewState>;
  private _viewState$ = new BehaviorSubject<RequisitionsViewState>(
    this.cacheService.getLocalStorageData(),
  );

  constructor() {
    this.viewState$ = this._viewState$.asObservable();
  }

  private get viewState(): RequisitionsViewState {
    return this._viewState$.getValue();
  }

  public initializeQueryParamsFromCache(): void {
    const { groupBy } = this.cacheService.getLocalStorageData();
    this.changeParams({ [RequisitionsListParam.GroupBy]: groupBy });
  }

  public setViewType(viewType: RequisitionsListViewType): void {
    this._viewState$.next({ ...this.viewState, viewType });
    this.cacheService.saveDataInLocalStorage({ viewType });
  }

  public setGroupVisibility(groupName: string): void {
    const currentCollapsedGroups = this.viewState.collapsedGroups;
    const collapsedGroups = currentCollapsedGroups.includes(groupName)
      ? currentCollapsedGroups.filter(group => group !== groupName)
      : [...currentCollapsedGroups, groupName];

    this._viewState$.next({ ...this.viewState, collapsedGroups });
    this.cacheService.saveDataInLocalStorage({ collapsedGroups });
  }

  public setGroup(groupBy: RequisitionsGroupKind | null): void {
    this.changeParams({ [RequisitionsListParam.GroupBy]: groupBy });
    this.cacheService.saveDataInLocalStorage({ groupBy });
  }

  public setSorting({ id, sort }: RequisitionsTableColumn): void {
    const nextSort: SortDirection =
      sort === SortDirection.Desc ? SortDirection.Asc : SortDirection.Desc;

    this.changeParams({
      [RequisitionsListParam.SortBy]: id,
      [RequisitionsListParam.SortOrder]: nextSort,
    });
  }

  public changeParams(queryParams: Partial<RequisitionsParams>) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }
}
